import { setOwn } from "../json";
import { OutputDecl, StepKind, TriggerKind } from "../registry/kinds";
import { ContextExtension, KindRegistry } from "../registry/registry";
import {
  FieldDef,
  NamedType,
  ObjectType,
  TypeRef,
  TypeSystem,
  createTypeSystem,
  named,
  nonNull,
  withNullable,
} from "../types/type-system";
import { GraphAnalysis } from "./analysis";
import {
  AutomationDefinition,
  ERROR_PORT,
  StepNode,
  TRIGGER_NODE_ID,
  stepFieldName,
} from "./automation";

export const RUN_INFO_TYPE: ObjectType = {
  kind: "object",
  name: "RunInfo",
  description: "The current workflow run",
  fields: {
    id: { type: nonNull("ID"), description: "Run id" },
    automationId: { type: nonNull("ID"), description: "Workflow id" },
    automationVersion: {
      type: nonNull("Int"),
      description: "Workflow version the run is pinned to",
    },
    startedAt: {
      type: nonNull("DateTime"),
      description: "When the run started",
    },
  },
};

export interface StepOutputInfo {
  nodeId: string;
  field: string;
  output: OutputDecl;
}

export interface AutomationTypes {
  ts: TypeSystem;
  triggerPayload: TypeRef;
  /** Root type of the context at a node: what its expressions can reference. */
  contextAt(nodeId: string): TypeRef;
  /** Context for automation policies (entry): trigger-time data only. */
  policyContext: TypeRef;
  outputs: ReadonlyMap<string, StepOutputInfo>;
  extensions: ContextExtension[];
}

export function contextTypeName(nodeId: string): string {
  return nodeId === TRIGGER_NODE_ID
    ? "Context_trigger"
    : `Context_${stepFieldName(nodeId)}`;
}

export function buildAutomationTypes(
  automation: AutomationDefinition,
  registry: KindRegistry,
  analysis: GraphAnalysis,
): AutomationTypes {
  const triggerKind = registry.triggers.get(automation.trigger.kind);
  if (!triggerKind) {
    throw new Error(`Unknown trigger kind ${automation.trigger.kind}`);
  }
  const payload = triggerKind.payload(automation.trigger.config);
  const extensions = registry.extensions.filter((e) =>
    e.applies(triggerKind, automation.trigger.config),
  );

  const outputs = new Map<string, StepOutputInfo>();
  const kinds = new Map<string, StepKind>();
  const outputTypes: NamedType[] = [];
  // Steps with an unknown kind or invalid settings: already reported, and their output is unknown.
  const broken = new Set<string>();
  for (const node of automation.nodes) {
    const kind = registry.steps.get(node.kind);
    if (!kind || !kind.parse(node.config).ok) {
      broken.add(node.id);
      continue;
    }
    kinds.set(node.id, kind);
    const output = kind.output(node.config);
    if (output) {
      outputs.set(node.id, {
        nodeId: node.id,
        field: stepFieldName(node.id),
        output,
      });
      outputTypes.push(...output.types);
    }
  }

  const baseFields: Record<string, FieldDef> = {
    trigger: {
      type: withNullable(payload.root, false),
      description: triggerKind.title,
      label: triggerKind.title,
    },
    run: {
      type: nonNull(RUN_INFO_TYPE.name),
      description: "The current workflow run",
    },
  };
  for (const ext of extensions) {
    setOwn(baseFields, ext.field, ext.def);
  }

  const contextTypes: ObjectType[] = [];
  const contextNames = new Map<string, TypeRef>();
  const addContext = (nodeId: string, stepFields: Record<string, FieldDef>) => {
    const stepsName = `Steps_${nodeId === TRIGGER_NODE_ID ? "trigger" : stepFieldName(nodeId)}`;
    contextTypes.push({
      kind: "object",
      name: stepsName,
      description: "Outputs of earlier steps",
      fields: stepFields,
    });
    const name = contextTypeName(nodeId);
    contextTypes.push({
      kind: "object",
      name,
      description: "Data available to this step",
      fields: {
        ...baseFields,
        steps: {
          type: nonNull(stepsName),
          description: "Outputs of earlier steps",
        },
      },
    });
    contextNames.set(nodeId, nonNull(name));
  };

  addContext(TRIGGER_NODE_ID, {});
  for (const node of automation.nodes) {
    const stepFields: Record<string, FieldDef> = {};
    for (const upstream of analysis.sometimes.get(node.id) ?? []) {
      if (broken.has(upstream)) {
        setOwn(stepFields, stepFieldName(upstream), {
          type: named("JSON"),
          description: "Output of a step whose settings have errors",
        });
        continue;
      }
      const info = outputs.get(upstream);
      const upstreamNode = automation.nodes.find((n) => n.id === upstream);
      const kind = kinds.get(upstream);
      if (!info || !upstreamNode || !kind) {
        continue;
      }
      const guaranteed = alwaysProducesOutput(
        automation,
        analysis,
        node.id,
        upstreamNode,
        kind,
      );
      setOwn(stepFields, info.field, {
        type: withNullable(
          info.output.root,
          !guaranteed || info.output.root.nullable,
        ),
        description: guaranteed
          ? `${upstreamNode.title ?? kind.title} output`
          : `${upstreamNode.title ?? kind.title} output (empty when that step didn't run)`,
        label: upstreamNode.title ?? kind.title,
      });
    }
    addContext(node.id, stepFields);
  }

  const ts = createTypeSystem(
    registry.types,
    payload.types,
    outputTypes,
    extensions.flatMap((e) => e.types),
    [RUN_INFO_TYPE],
    contextTypes,
  );
  const policyContext =
    contextNames.get(TRIGGER_NODE_ID) ??
    nonNull(contextTypeName(TRIGGER_NODE_ID));
  return {
    ts,
    triggerPayload: payload.root,
    contextAt: (nodeId) => contextNames.get(nodeId) ?? policyContext,
    policyContext,
    outputs,
    extensions,
  };
}

/** Whether `upstream` always ran by `nodeId`: on every path, not skippable (only those honor `disabled`), no error port. */
export function alwaysProducesOutput(
  automation: AutomationDefinition,
  analysis: GraphAnalysis,
  nodeId: string,
  upstream: StepNode,
  kind: StepKind,
): boolean {
  return (
    (analysis.always.get(nodeId)?.has(upstream.id) ?? false) &&
    !kind.skippable &&
    !automation.edges.some(
      (e) => e.from === upstream.id && e.port === ERROR_PORT,
    )
  );
}

export function triggerKindOf(
  automation: AutomationDefinition,
  registry: KindRegistry,
): TriggerKind | undefined {
  return registry.triggers.get(automation.trigger.kind);
}
